import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../../../../hooks/use-is-mounted";
import {deepEqual} from "../../../../../../../../../../core/services/utils/utils";
import {OutlinedInput} from "../../../../../../../../../components/base/input/outlined_input";
import SaveOrRollback from "../../../../../../../../../components/app-specific/save-or-rollback";
import Api from "../../../../../../../../../../core/services/api/api";


const FreeExceptionTypeIds = {
    totalOrder: 1,
    profitMargin: 2,
    rangeException: 3,
}

const FreeShippingFeesManagementViewSettingsGeneralExceptions = ({
                                                                     syncWithDetachedCustomers,
                                                                     customerId,
                                                                     customerName,
                                                                     data: dataProp,
                                                                     refresher,
                                                                 }) => {
    const [profitMargin, setProfitMargin] = useState({initialValue: '', value: ''});
    const [totalOrder, setTotalOrder] = useState({initialValue: '', value: ''});
    // const [submittingProfitMargin, setSubmittingProfitMargin] = useState(false);
    const [submittingTotalOrder, setSubmittingTotalOrder] = useState(false);
    const isMounted = useIsMounted();

    const isStaticData = useMemo(() => !!dataProp?.length, [dataProp?.length])

    /**
     * Sets the value of the profit margin.
     *
     * * if [setInitial] arg flag is set, then sets the initialValue with the provided value as well.
     * @type {(function(*, boolean=): void)|*}
     */
    const setProfitMarginValue = useCallback((value, setInitial = false) => {
        value = value ?? "";
        setProfitMargin(prevState => ({
            ...prevState,
            value: value,
            ...(setInitial ? {initialValue: value} : {})
        }))
    }, [])

    /**
     * Sets the value of the total order.
     *
     * * if [setInitial] arg flag is set, then sets the initialValue with the provided value as well.
     * @type {(function(*, boolean=): void)|*}
     */
    const setTotalOrderValue = useCallback((value, setInitial = false) => {
        value = value ?? "";
        setTotalOrder(prevState => ({
            ...prevState,
            value: value,
            ...(setInitial ? {initialValue: value} : {})
        }))
    }, [])

    /**
     * Sets the general exceptions values from the provided data.
     * @type {(function(*): void)|*}
     */
    const setFreeShippingFeesGeneralExceptions = useCallback((data) => {
        for (const exception of data) {
            switch (exception.freeExceptionTypeId) {
                case FreeExceptionTypeIds.profitMargin:
                    setProfitMarginValue(exception.threshold?.toString() ?? '', true);
                    break;
                case FreeExceptionTypeIds.totalOrder:
                    setTotalOrderValue(exception.threshold?.toString() ?? '', true);
                    break;
                default:
                    break;
            }
        }
    }, [setProfitMarginValue, setTotalOrderValue]);

    /**
     * Fetches the general exceptions values from the server for free shipping settings view.
     * @type {(function(): Promise<void>)|*}
     */
    const getFreeShippingFeesGeneralExceptions = useCallback(async () => {
        if (isStaticData) {
            return setFreeShippingFeesGeneralExceptions(dataProp);
        }
        const response = await Api.getFreightFlatExceptions(customerId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setFreeShippingFeesGeneralExceptions(response.data);
        }
    }, [isStaticData, customerId, isMounted, setFreeShippingFeesGeneralExceptions, dataProp]);

    /**
     * As soon as the component mounts:
     * - fetches the general exceptions values from the server for free shipping settings view.
     */
    useEffect(() => {
        getFreeShippingFeesGeneralExceptions().then();
    }, [getFreeShippingFeesGeneralExceptions, refresher]);

    // const profitMarginChanged = useMemo(() => ({
    //     cancel: !deepEqual(profitMargin.value, profitMargin.initialValue),
    //     save: !deepEqual(profitMargin.value, profitMargin.initialValue) && !!profitMargin.value,
    // }), [profitMargin.value, profitMargin.initialValue]);
    //
    // /**
    //  * Reverts the value of the profit margin with its initial value.
    //  * @type {(function(): void)|*}
    //  */
    // const revertProfitMarginValue = useCallback(() => {
    //     setProfitMargin(prevState => ({...prevState, value: prevState.initialValue}))
    // }, [setProfitMarginValue])
    //
    // /**
    //  * Updates the value of profit margin in the server.
    //  * @type {(function(): Promise<void>)|*}
    //  */
    // const updateProfitMargin = useCallback(async () => {
    //     const forApi = {
    //         freeExceptionTypeId: FreeExceptionTypeIds.profitMargin,
    //         threshold: Number(profitMargin.value),
    //         applyToAll: syncWithDetachedCustomers,
    //         customerOriginalId: customerId,
    //         customerTitle: customerName,
    //     }
    //     setSubmittingProfitMargin(true);
    //     const response = await Api.saveFreightFlatExceptions(forApi);
    //     if (!isMounted())
    //         return;
    //     setSubmittingProfitMargin(false);
    //     if (response?.resultFlag) {
    //         setProfitMarginValue(profitMargin.value, true);
    //     }
    // }, [profitMargin.value, syncWithDetachedCustomers, customerId, customerName, isMounted, setProfitMarginValue])

    const totalOrderChanged = useMemo(() => ({
        cancel: !deepEqual(totalOrder.value, totalOrder.initialValue),
        save: !deepEqual(totalOrder.value, totalOrder.initialValue) && !!totalOrder.value,
    }), [totalOrder.value, totalOrder.initialValue]);

    /**
     * Reverts the value of total-order with its initial value.
     * @type {(function(): void)|*}
     */
    const revertTotalOrderValue = useCallback(() => {
        setTotalOrder(prevState => ({...prevState, value: prevState.initialValue}))
    }, [])

    /**
     * Updates the value of total-order in the server.
     * @type {(function(): Promise<void>)|*}
     */
    const updateTotalOrder = useCallback(async () => {
        const forApi = {
            freeExceptionTypeId: FreeExceptionTypeIds.totalOrder,
            threshold: Number(totalOrder.value),
            applyToAll: syncWithDetachedCustomers,
            customerOriginalId: customerId,
            customerTitle: customerName,
        }
        setSubmittingTotalOrder(true);
        const response = await Api.saveFreightFlatExceptions(forApi);
        if (!isMounted())
            return;
        setSubmittingTotalOrder(false);
        if (response?.resultFlag) {
            setTotalOrderValue(totalOrder.value, true);
        }
    }, [totalOrder.value, syncWithDetachedCustomers, customerId, customerName, isMounted, setTotalOrderValue])


    return (
        <>
            <div className={'section'}>
                <p className={'section-title'}>
                    General
                </p>
                <div className={'section-paper'}>
                    {/*<p className={'text-md font-weight-semi-bold mb-2'}>*/}
                    {/*    Profit Margin*/}
                    {/*</p>*/}
                    {/*<div className={'inline-section'}>*/}
                    {/*    Orders less than*/}
                    {/*    <div className={'d-inline-flex align-items-center'}>*/}
                    {/*        <OutlinedInput*/}
                    {/*            fullWidth={false}*/}
                    {/*            size={'small'}*/}
                    {/*            value={profitMargin.value}*/}
                    {/*            onChange={setProfitMarginValue}*/}
                    {/*            type={'number'}*/}
                    {/*            className={'ml-2'}*/}
                    {/*        />*/}
                    {/*        <SaveOrRollback*/}
                    {/*            show={profitMarginChanged}*/}
                    {/*            column={false}*/}
                    {/*            showText={false}*/}
                    {/*            loading={submittingProfitMargin}*/}
                    {/*            onRollback={revertProfitMarginValue}*/}
                    {/*            onSave={updateProfitMargin}*/}
                    {/*        />*/}
                    {/*    </div>*/}
                    {/*    profit margin are not eligible.*/}
                    {/*</div>*/}
                    <p className={'text-md font-weight-semi-bold mb-2'}>
                        Total Order
                    </p>
                    <div className={'inline-section'}>
                        Orders less than
                        <div className={'d-inline-flex align-items-center'}>
                            <OutlinedInput
                                fullWidth={false}
                                size={'small'}
                                value={totalOrder.value}
                                onChange={setTotalOrderValue}
                                type={'number'}
                                className={'ml-2'}
                            />
                            <SaveOrRollback
                                show={totalOrderChanged}
                                column={false}
                                showText={false}
                                loading={submittingTotalOrder}
                                onRollback={revertTotalOrderValue}
                                onSave={updateTotalOrder}
                            />
                        </div>
                        in total are not eligible.
                    </div>
                </div>
            </div>
        </>
    );
}

export default FreeShippingFeesManagementViewSettingsGeneralExceptions;
